// Browser-side PDF text extraction (pdf.js).
// Works for text-layer bank statements (i.e. already-OCR'd/searchable PDFs).
// Text items are grouped into visual rows by Y coordinate and joined by X,
// which reconstructs table rows much better than a flat item dump.
//
// Password-protected PDFs: extraction returns a status instead of throwing,
// so the UI can prompt for a password and retry with it.

import * as pdfjsLib from 'pdfjs-dist';
// Vite resolves this to the bundled worker asset URL.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

interface TextItem {
  str: string;
  x: number;
  y: number;
}

/** Result of a PDF extraction attempt. */
export type PdfExtractionResult =
  | { status: 'ok'; text: string }
  | { status: 'password_needed' } // locked PDF, no password supplied yet
  | { status: 'incorrect_password' }; // supplied password was rejected

function isPasswordException(e: unknown): e is Error & { code: number } {
  return (
    e instanceof Error &&
    e.name === 'PasswordException' &&
    typeof (e as Error & { code?: unknown }).code === 'number'
  );
}

/**
 * Extract text from a (possibly password-protected) PDF.
 *
 * Locked PDFs do not throw: they resolve with `password_needed` /
 * `incorrect_password` so the caller can prompt the user and retry with
 * the password. Any other failure (corrupt file, scanned image) still throws.
 *
 * pdf.js password semantics:
 *   - no password given + encrypted doc   → PasswordException code 1 (NEED_PASSWORD)
 *   - wrong password given                → PasswordException code 2 (INCORRECT_PASSWORD)
 */
export async function extractTextFromPdf(
  file: File,
  password?: string,
): Promise<PdfExtractionResult> {
  const data = await file.arrayBuffer();

  const params: Parameters<typeof pdfjsLib.getDocument>[0] = { data };
  if (password) params.password = password;

  let doc: pdfjsLib.PDFDocumentProxy;
  try {
    doc = await pdfjsLib.getDocument(params).promise;
  } catch (e) {
    if (isPasswordException(e)) {
      // A NEED_PASSWORD(1) failure while a password was supplied
      // effectively means the password was rejected too.
      return e.code === 2 || Boolean(password)
        ? { status: 'incorrect_password' }
        : { status: 'password_needed' };
    }
    throw e;
  }

  console.log(`[pdf] loaded ${doc.numPages} page(s) from "${file.name}"`);

  let out = '';
  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();

    // Group items into visual rows (same Y within a small tolerance).
    const rows: Array<{ y: number; items: TextItem[] }> = [];
    for (const it of content.items as Array<{ str?: string; transform?: number[] }>) {
      if (!it.str || !it.transform) continue;
      const [, , , , x, y] = it.transform;
      let row = rows.find((r) => Math.abs(r.y - y) < 3);
      if (!row) {
        row = { y, items: [] };
        rows.push(row);
      }
      row.items.push({ str: it.str, x, y });
    }

    // Top-to-bottom rows, left-to-right items within a row.
    rows.sort((a, b) => b.y - a.y);
    out += rows
      .map((r) => r.items.sort((a, b) => a.x - b.x).map((i) => i.str).join(' '))
      .join('\n');
    out += '\n';
  }

  console.log(
    `[pdf] extracted ${out.length} chars (first 120: "${out.slice(0, 120).replace(/\n/g, ' ⏎ ')}")`,
  );
  return { status: 'ok', text: out };
}
